import * as React from "react";
import { Link, useLocation } from "react-router-dom";
import unsamLogo from "../../../assets/img/unsam.png";
import useAuth from "../../main/Auth/useAuth";



function NavLink({ to, label, icon, danger, onClick, isActive }) {
    const baseClasses = "flex items-center gap-4 rounded-2xl px-4 py-3.5 text-sm font-bold no-underline transition-all duration-300 relative overflow-hidden group";

    const activeClasses = isActive
        ? "text-brand-navy shadow-brand-gold/20"
        : danger
            ? "text-red-600 hover:bg-red-50"
            : "text-slate-600 hover:bg-brand-soft hover:text-brand-navy";

    const iconBase = "inline-flex h-10 w-10 items-center justify-center rounded-xl text-[16px] transition-all duration-300 relative z-10";
    const iconActive = isActive
        ? "bg-gradient-to-br from-brand-gold to-yellow-400 text-brand-navy shadow-md"
        : danger
            ? "bg-red-100 text-red-600"
            : "bg-white text-slate-400 shadow-sm border border-slate-100 group-hover:text-brand-gold group-hover:border-brand-gold/30 group-hover:shadow-brand-gold/10";

    return (
        <Link to={to} onClick={onClick} className={`${baseClasses} ${activeClasses}`}>
            {isActive && (
                <div className="absolute inset-0 bg-gradient-to-r from-brand-gold/20 via-brand-gold/5 to-transparent rounded-2xl z-0 border border-brand-gold/30"></div>
            )}
            <span className={`${iconBase} ${iconActive}`}>
                <i className={`fas ${icon}`} />
            </span>
            <span className="relative z-10">{label}</span>
        </Link>
    );
}

export default function Navbar({ title }) {
    const [open, setOpen] = React.useState(false);
    const location = useLocation();
    const { user } = useAuth();

    const menuItems = [
        { to: "/dashboard", label: "Dashboard", icon: "fa-home" },
        { to: "/data", label: "Data Publikasi", icon: "fa-database" },
    ];

    if (user && (user.level === 1 || String(user.level) === '1')) {
        menuItems.push({ to: "/reviewer/dashboard", label: "Review Publikasi", icon: "fa-user-check" });
    }

    const sidebar = (
        <div className="relative flex h-full flex-col bg-white/95 backdrop-blur-3xl overflow-hidden shadow-2xl">

            <div className="absolute -bottom-20 -right-20 h-64 w-64 rounded-full bg-gradient-to-tl from-brand-gold/30 to-transparent blur-3xl pointer-events-none z-0"></div>

            <div className="relative z-10 flex h-24 items-center gap-4 border-b border-white/50 px-6 pt-2">
                <div className="relative flex h-12 w-12 items-center justify-center rounded-2xl bg-white shadow-xl shadow-brand-gold/10 border border-brand-gold/20">
                    <img
                        src={unsamLogo}
                        className="h-8 w-8 object-contain"
                        alt="Universitas Samudra"
                    />
                </div>
                <div className="flex flex-col">
                    <span className="font-display font-extrabold text-brand-navy text-[16px] tracking-tight">
                        Repositori Publikasi
                    </span>
                    <span className="font-display text-[10px] font-bold uppercase tracking-widest text-brand-gold">
                        Universitas Samudra
                    </span>
                </div>
            </div>
            <nav className="relative z-10 flex-1 space-y-3 overflow-y-auto px-2 py-8">
                <div className="mb-4 px-2 text-[10px] font-extrabold uppercase tracking-widest text-slate-400">Menu Utama</div>
                {menuItems.map((item) => (
                    <NavLink
                        key={item.to}
                        {...item}
                        isActive={
                            location.pathname === item.to ||
                            (item.to !== '/' && location.pathname.startsWith(item.to + '/')) ||
                            (item.to.startsWith('/reviewer') && location.pathname.startsWith('/reviewer'))
                        }
                        onClick={() => setOpen(false)}
                    />
                ))}
            </nav>
            <div className="relative z-10 border-t border-white/50 bg-white/50 p-5 backdrop-blur-md">
                <NavLink
                    to="/logout"
                    label="Logout"
                    icon="fa-sign-out-alt"
                    danger
                    onClick={() => setOpen(false)}
                />
            </div>
        </div>
    );

    return (
        <>
            <aside className="hidden w-[312px] shrink-0 md:block z-40 relative">
                <div className="fixed inset-y-4 left-4 w-[280px] rounded-[2.5rem] overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-white">
                    {sidebar}
                </div>
            </aside>

            <header className="fixed left-4 right-4 top-4 z-30 flex h-[76px] items-center justify-between rounded-full border border-white/80 bg-white/70 px-6 text-brand-navy backdrop-blur-xl transition-all sm:px-6 md:left-[312px] lg:px-8 overflow-hidden">
                <div className="absolute inset-y-0 right-0 w-1/2 bg-gradient-to-l from-brand-gold/20 to-transparent blur-2xl pointer-events-none z-0"></div>

                <div className="flex items-center gap-4 relative z-10">
                    <button
                        type="button"
                        onClick={() => setOpen(true)}
                        className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-white/60 bg-white/50 text-slate-600 shadow-sm backdrop-blur-md transition hover:bg-white hover:text-brand-gold md:hidden"
                        aria-label="Buka menu"
                    >
                        <i className="fas fa-bars text-lg"></i>
                    </button>
                    <h1 className="font-display text-2xl font-extrabold tracking-tight text-brand-navy">{title}</h1>
                </div>

                <div className="ml-auto flex items-center gap-4 relative z-10">
                    <div className="hidden flex-col text-right sm:flex">
                        <strong className="font-display text-sm font-extrabold tracking-tight text-brand-navy">{user.name}</strong>
                        <span className="font-display text-[10px] font-bold tracking-widest uppercase text-brand-gold">{user.username}</span>
                    </div>
                    <div className="relative flex h-12 w-12 cursor-pointer items-center justify-center rounded-full bg-gradient-to-br from-brand-gold to-yellow-500 p-[3px] shadow-lg shadow-brand-gold/30 transition hover:scale-105">
                        <div className="flex h-full w-full items-center justify-center rounded-full border-2 border-white bg-white overflow-hidden">
                            {user?.profile?.foto ? (
                                <img src={user?.profile?.foto} alt={user?.name} className="h-full w-full object-cover" />
                            ) : (
                                <i className="fas fa-user text-brand-gold"></i>
                            )}
                        </div>
                    </div>
                </div>
            </header>

            {/* Overlay Mobile */}
            {open ? (
                <div
                    className="fixed inset-0 z-50 flex bg-brand-navy/60 p-4 backdrop-blur-sm transition-opacity md:hidden"
                    onClick={() => setOpen(false)}
                >
                    <div
                        className="h-full w-[280px] rounded-[2.5rem] overflow-hidden shadow-2xl transition-transform"
                        onClick={(event) => event.stopPropagation()}
                    >
                        {sidebar}
                    </div>
                </div>
            ) : null}
        </>
    );
}
